import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Markdown } from "tiptap-markdown";
import { styled } from "@mui/system";
import CustomLink from "components/editor/extensions/CustomLink";
import LinkBubbleMenu from "components/editor/LinkBubbleMenu";

const StyledEditor = styled(EditorContent)({
  "& .ProseMirror": {
    outline: "none",
    wordBreak: "break-word",
    color: "#6B7280",
    fontSize: "13px",
    lineHeight: 1.4,
  },
  "& .ProseMirror p": {
    margin: 0,
  },
  "& .ProseMirror a": {
    color: "#198282",
    fontWeight: 600,
    textDecoration: "none",
    cursor: "pointer",
  },
  "& .ProseMirror-focused": {
    outline: "none",
  },
  ".ProseMirror p.is-editor-empty:first-of-type::before": {
    color: "#adb5bd",
    content: "attr(data-placeholder)",
    float: "left",
    height: 0,
    pointerEvents: "none",
  },
});

const extensions = (editable: boolean, placeholder?: string) => [
  StarterKit.configure({
    heading: false,
    codeBlock: false,
    horizontalRule: false,
  }),
  CustomLink.configure({
    openOnClick: !editable,
    autolink: true,
    linkOnPaste: true,
  }),
  ...(placeholder
    ? [Placeholder.configure({ placeholder, showOnlyWhenEditable: true })]
    : []),
  Markdown.configure({
    html: false,
    linkify: true,
    breaks: true,
    transformPastedText: true,
    transformCopiedText: true,
  }),
];

export default function MarkdownMiniEditor({
  value,
  onChange,
  placeholder,
  editable = true,
}: {
  value: string;
  onChange?: (markdown: string) => void;
  placeholder?: string;
  editable?: boolean;
}) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    editable,
    extensions: extensions(editable, placeholder),
    content: value,
    onUpdate: ({ editor }) => {
      onChangeRef.current?.(editor.storage.markdown.getMarkdown());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const currentMd = editor.storage.markdown.getMarkdown();
    if (value !== currentMd) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <>
      {editable && <LinkBubbleMenu editor={editor} />}
      <StyledEditor editor={editor} />
    </>
  );
}
